'use client'

import { usePathname, useRouter } from 'next/navigation'
import { MouseEvent, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'

import { addedProductToCartEvent, useCart, useProductGifts } from '@/entities/cart'
import { useCartItemsData } from '@/entities/cart/hooks/use-cart-items-data'

import { CatalogItemLocation, ProductType } from '@/shared/types/product'
import { TProduct } from '@/shared/types/product-types'
import { Button } from '@/shared/ui'

interface AddToCartProps {
	className?: string
	location?: CatalogItemLocation
	productData: Partial<TProduct>
	size?: 'S' | 'M' | 'L'
	fullWidth?: boolean
	buttonName?: string
	isPending?: boolean
	onAddStarted?: (addPromise: Promise<unknown>) => void
	onInCartClick?: () => boolean | Promise<boolean>
}

export const AddToCart = (props: AddToCartProps) => {
	const {
		productData,
		location = CatalogItemLocation.None,
		size = 'M',
		fullWidth,
		className,
		buttonName,
		isPending = false,
		onAddStarted,
		onInCartClick
	} = props
	const { ID, KOD, TITLE, PRICE, SECTION_ID, SECTION_NAME } = productData
	const { checkOneProductForGifts } = useProductGifts()
	const [isLoading, setIsLoading] = useState(false)

	const router = useRouter()
	const pathname = usePathname()
	const dispatch = useDispatch()
	const { addItem } = useCart()

	const { getBasketItem } = useCartItemsData()
	const isItemInCart = useMemo(() => getBasketItem(ID as number), [getBasketItem, ID])

	const payloadData = {
		ID,
		KOD,
		PRICE,
		TYPE_PRODUCTS: ProductType.Product,
		SECTION_ID,
		SECTION_NAME,
		TITLE,
		location
	}

	const onClick = async (e: MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation()
		e.preventDefault()
		setIsLoading(true)

		try {
			const updateBasket = pathname === `/cart`
			const addPromise = addItem({ ...payloadData, updateBasket })
			onAddStarted?.(addPromise)
			await addPromise

			if (ID) {
				const { one } = await checkOneProductForGifts(ID)
				dispatch(addedProductToCartEvent({ productId: ID, gift: one?.[0], location }))
			}
		} finally {
			setIsLoading(false)
		}
	}

	const toCart = async (e: MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation()
		if (onInCartClick && (await onInCartClick())) return

		await router.push(`/cart`)
	}

	if (isItemInCart)
		return (
			<Button
				onClick={toCart}
				theme="outlined"
				size={size}
				fullWidth={fullWidth}
				className={className}
				isLoading={isPending}
				disabled={isPending}>
				{buttonName ?? 'Оформить'}
			</Button>
		)

	return (
		<Button
			onClick={onClick}
			size={size}
			fullWidth={fullWidth}
			className={className}
			isLoading={isLoading || isPending}
			disabled={isLoading || isPending}>
			{buttonName ?? 'В корзину'}
		</Button>
	)
}
